import React, { useState, useEffect } from 'react';
import { Modal, Form, Button, Alert, Row, Col, Spinner } from 'react-bootstrap';
import api from '../../services/api';

/**
 * Modal for creating and editing associations manually
 */
const AssociationCreationModal = ({ show, onHide, association = null, onSuccess }) => {
  const isEdit = Boolean(association?.id);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  // Form data state
  const [formData, setFormData] = useState({
    association_code: '',
    name: '',
    address: '',
    street_number: '',
    zip_code: '',
    city: '',
    province: '',
    phone: '',
    president_first_name: '',
    president_last_name: '',
    email: '',
    territorial: '',
    regional: '',
    fiscal_code: ''
  });

  // Form validation
  const [formErrors, setFormErrors] = useState({});

  // Load association data if editing. Creation is disabled in this modal.
  useEffect(() => {
    if (association) {
      setFormData({
        association_code: association.association_code || '',
        name: association.name || '',
        address: association.address || '',
        street_number: association.street_number || '',
        zip_code: association.zip_code || '',
        city: association.city || '',
        province: association.province || '',
        phone: association.phone || '',
        president_first_name: association.president_first_name || '',
        president_last_name: association.president_last_name || '',
        email: association.email || '',
        territorial: association.territorial || '',
        regional: association.regional || '',
        fiscal_code: association.fiscal_code || ''
      });
      setError(null);
    } else {
      // Creation disabled: show informative error and keep form disabled until an association is provided
      setError("Creazione di nuove associazioni disabilitata. Apri il modal con un'associazione da modificare.");
    }
  }, [association, show]);

  const resetForm = () => {
    setFormData({
      association_code: '',
      name: '',
      address: '',
      street_number: '',
      zip_code: '',
      city: '',
      province: '',
      phone: '',
      president_first_name: '',
      president_last_name: '',
      email: '',
      territorial: '',
      regional: '',
      fiscal_code: ''
    });
    setFormErrors({});
    setError(null);
    setSuccess(false);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));

    // Clear validation error when field is edited
    if (formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const validateForm = () => {
    const errors = {};

    // Required fields
    if (!formData.association_code.trim()) {
      errors.association_code = 'Il codice associazione è obbligatorio';
    }

    if (!formData.name.trim()) {
      errors.name = 'Il nome associazione è obbligatorio';
    }

    // Email format validation if provided
    if (formData.email && !/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = 'Formato email non valido';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(false);

    try {
      if (!isEdit) {
        // Creation is disabled in this modal
        setLoading(false);
        setError("Creazione disabilitata. Apri il modal con un'associazione da modificare.");
        return;
      }

      // Update existing association
      const response = await api.put(`/admin/associations/${association.id}`, formData);

      setSuccess(true);
      setLoading(false);

      // Notify parent component of success
      if (onSuccess) {
        onSuccess(response.data.association);
      }

      // Auto-close after successful update (optional)
      setTimeout(() => {
        onHide();
      }, 1500);

    } catch (err) {
      setLoading(false);
      setError(err.response?.data?.message || 'Si è verificato un errore. Riprova più tardi.');
    }
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title>Modifica Associazione</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && <Alert variant="danger">{error}</Alert>}
        {success && <Alert variant="success">Associazione aggiornata con successo!</Alert>}

  <Form onSubmit={handleSubmit}>
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Codice Associazione*</Form.Label>
                <Form.Control
                  type="text"
                  name="association_code"
                  value={formData.association_code}
                  onChange={handleInputChange}
                  isInvalid={!!formErrors.association_code}
                  disabled={isEdit} // Can't change code if editing
                />
                <Form.Control.Feedback type="invalid">
                  {formErrors.association_code}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Nome Associazione*</Form.Label>
                <Form.Control
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  isInvalid={!!formErrors.name}
                />
                <Form.Control.Feedback type="invalid">
                  {formErrors.name}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col md={8}>
              <Form.Group className="mb-3">
                <Form.Label>Indirizzo</Form.Label>
                <Form.Control
                  type="text"
                  name="address"
                  value={formData.address}
                  onChange={handleInputChange}
                />
              </Form.Group>
            </Col>

            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label>Numero Civico</Form.Label>
                <Form.Control
                  type="text"
                  name="street_number"
                  value={formData.street_number}
                  onChange={handleInputChange}
                />
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label>CAP</Form.Label>
                <Form.Control
                  type="text"
                  name="zip_code"
                  value={formData.zip_code}
                  onChange={handleInputChange}
                />
              </Form.Group>
            </Col>

            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label>Città</Form.Label>
                <Form.Control
                  type="text"
                  name="city"
                  value={formData.city}
                  onChange={handleInputChange}
                />
              </Form.Group>
            </Col>

            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label>Provincia</Form.Label>
                <Form.Control
                  type="text"
                  name="province"
                  value={formData.province}
                  onChange={handleInputChange}
                />
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Telefono</Form.Label>
                <Form.Control
                  type="text"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                />
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Email</Form.Label>
                <Form.Control
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  isInvalid={!!formErrors.email}
                />
                <Form.Control.Feedback type="invalid">
                  {formErrors.email}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Nome Presidente</Form.Label>
                <Form.Control
                  type="text"
                  name="president_first_name"
                  value={formData.president_first_name}
                  onChange={handleInputChange}
                />
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Cognome Presidente</Form.Label>
                <Form.Control
                  type="text"
                  name="president_last_name"
                  value={formData.president_last_name}
                  onChange={handleInputChange}
                />
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label>Codice Fiscale</Form.Label>
                <Form.Control
                  type="text"
                  name="fiscal_code"
                  value={formData.fiscal_code}
                  onChange={handleInputChange}
                />
              </Form.Group>
            </Col>

            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label>Territoriale</Form.Label>
                <Form.Control
                  type="text"
                  name="territorial"
                  value={formData.territorial}
                  onChange={handleInputChange}
                />
              </Form.Group>
            </Col>

            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label>Regionale</Form.Label>
                <Form.Control
                  type="text"
                  name="regional"
                  value={formData.regional}
                  onChange={handleInputChange}
                />
              </Form.Group>
            </Col>
          </Row>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide} disabled={loading}>
          Annulla
        </Button>
        <Button variant="primary" onClick={handleSubmit} disabled={loading || !isEdit}>
          {loading ? (
            <>
              <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-2" />
              Aggiornamento...
            </>
          ) : (
            'Aggiorna Associazione'
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default AssociationCreationModal;
